
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Helmet } from "react-helmet-async";
import { 
  ChevronRight, 
  Search,
  BookOpen, 
  FileText, 
  Code, 
  Video, 
  Check, 
  ArrowRight,
  Copy,
  Globe,
  Terminal,
  Database,
  BookMarked,
  HelpCircle,
  Menu,
  File
} from "lucide-react";

const DocumentationPage = () => {
  const { t } = useTranslation("common");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTutorial, setActiveTutorial] = useState("getting-started");
  
  // Animation controls for scroll animations
  const controls = useAnimation();
  const guidesRef = useRef(null);
  const guidesInView = useInView(guidesRef, { once: true });
  
  useEffect(() => {
    document.title = "Documentation & Guides | Verdea";
    
    if (guidesInView) {
      controls.start("visible");
    }
  }, [controls, guidesInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const docCategories = [
    {
      title: "User Guides",
      description: "Learn how to use Verdea's features and functionalities",
      icon: BookOpen,
      items: [
        { title: "Getting Started Guide", path: "#getting-started" },
        { title: "Dashboard Overview", path: "#dashboard" },
        { title: "Data Import & Export", path: "#data-import" },
        { title: "Report Generation", path: "#report-generation" },
        { title: "User Management", path: "#user-management" }
      ]
    },
    {
      title: "API Documentation",
      description: "Integrate Verdea with your own systems and workflows",
      icon: Code,
      items: [
        { title: "API Overview", path: "/api" },
        { title: "Authentication", path: "/api#authentication" },
        { title: "Endpoints Reference", path: "/api#endpoints" },
        { title: "Rate Limits", path: "/api#rate-limits" },
        { title: "Webhooks", path: "/api#webhooks" }
      ]
    },
    {
      title: "CSRD Resources",
      description: "Understand CSRD requirements and compliance strategies",
      icon: FileText,
      items: [
        { title: "CSRD Explained", path: "#csrd-explained" },
        { title: "ESRS Standards Overview", path: "#esrs-standards" },
        { title: "Compliance Timeline", path: "#compliance-timeline" },
        { title: "Materiality Assessment", path: "#materiality" },
        { title: "Data Collection Best Practices", path: "#data-collection" }
      ]
    }
  ];

  const videoTutorials = [
    {
      id: "getting-started",
      title: "Getting Started with Verdea",
      description: "A comprehensive introduction to the Verdea platform and its features",
      duration: "10 min",
      thumbnail: "/screens/csrdatlas_knowledge_base.png",
      videoUrl: "https://example.com/videos/getting-started"
    },
    {
      id: "data-import",
      title: "Importing Your Sustainability Data",
      description: "Learn how to import your existing sustainability data into Verdea",
      duration: "8 min",
      thumbnail: "/screens/dashboard_overview.png",
      videoUrl: "https://example.com/videos/data-import"
    },
    {
      id: "report-generation",
      title: "Generating CSRD-Compliant Reports",
      description: "Step-by-step guide to creating CSRD-compliant sustainability reports",
      duration: "12 min",
      thumbnail: "/screens/csrdatlas_knowledge_article.png",
      videoUrl: "https://example.com/videos/report-generation"
    },
    {
      id: "advanced-features",
      title: "Advanced Features and Customization",
      description: "Explore advanced features and customization options in Verdea",
      duration: "15 min",
      thumbnail: "/screens/aifguard_dashboard.png",
      videoUrl: "https://example.com/videos/advanced-features"
    }
  ];

  const quickGuides = [
    {
      title: "API Authentication",
      description: "How to authenticate your API requests",
      icon: Terminal,
      path: "/api#authentication"
    },
    {
      title: "Data Integration",
      description: "Connect Verdea with your existing systems",
      icon: Database,
      path: "#data-integration"
    },
    {
      title: "CSRD Requirements",
      description: "Overview of CSRD reporting requirements",
      icon: BookMarked,
      path: "#csrd-requirements"
    },
    {
      title: "Troubleshooting",
      description: "Solutions for common issues",
      icon: HelpCircle,
      path: "/help/troubleshooting"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Documentation & Guides | Verdea</title>
        <meta name="description" content="Comprehensive documentation, guides, and resources for using Verdea's CSRD compliance platform" />
      </Helmet>
      
      <NavBar />
      
      <div className="flex min-h-screen">
        {/* Sidebar Navigation - Hidden on mobile */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 h-screen sticky top-0 pt-24 pb-10 overflow-y-auto">
          <div className="px-6 py-4">
            <div className="text-sm font-medium text-slate-500 mb-6">DOCUMENTATION</div>
            <nav className="space-y-8">
              {docCategories.map((category, i) => (
                <div key={i}>
                  <div className="flex items-center gap-2 mb-3 text-emerald-700 font-medium">
                    <category.icon className="h-4 w-4" />
                    <span>{category.title}</span>
                  </div>
                  <ul className="space-y-2 ml-6">
                    {category.items.map((item, j) => (
                      <li key={j}>
                        <a href={item.path} className="text-slate-700 hover:text-emerald-700 transition-colors text-sm">
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>
        
        <div className="flex-1">
          {/* Main Content */}
          <main className="pt-24">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white py-16">
              <div className="absolute inset-0 z-0 opacity-40">
                <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                      <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(15, 61, 43, 0.05)" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>
              
              <div className="container mx-auto px-4 relative z-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center max-w-3xl mx-auto"
                >
                  <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
                    Documentation
                  </h1>
                  <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                    Comprehensive guides, tutorials, and resources for getting the most out of Verdea
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="max-w-2xl mx-auto relative mb-6"
                >
                  <div className="relative">
                    <Input
                      type="search"
                      placeholder="Search documentation..."
                      className="w-full h-14 pl-12 pr-4 rounded-xl shadow-sm"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-emerald-700" size={20} />
                    <Button 
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-emerald-700 hover:bg-emerald-800 text-white h-10 px-4"
                      size="sm"
                    >
                      Search
                    </Button>
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="flex justify-center flex-wrap gap-3 max-w-3xl mx-auto"
                >
                  {["Getting Started", "API", "User Guide", "CSRD", "Tutorials"].map((tag, i) => (
                    <button 
                      key={i}
                      className="px-4 py-1.5 rounded-full bg-white shadow-sm border border-slate-200 text-sm text-slate-700 hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                      onClick={() => setSearchQuery(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </motion.div>
              </div>
            </section>

            {/* Documentation Categories */}
            <section className="py-16 bg-white">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center max-w-3xl mx-auto mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-slate-900">
                    Browse Documentation
                  </h2>
                  <p className="text-lg text-slate-600">
                    Find guides, tutorials, and resources to help you get the most out of Verdea
                  </p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                  {docCategories.map((category, index) => {
                    const Icon = category.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
                      >
                        <div className="mb-4 p-3 bg-emerald-50 w-fit rounded-lg">
                          <Icon className="h-7 w-7 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-slate-900">{category.title}</h3>
                        <p className="text-slate-600 mb-6">{category.description}</p>
                        <ul className="space-y-2 mb-6">
                          {category.items.slice(0, 3).map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="h-1 w-1 bg-emerald-500 rounded-full mt-3 mr-2"></div>
                              <a href={item.path} className="text-slate-700 hover:text-emerald-700 transition-colors">
                                {item.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                        <a href={category.items[0].path} className="text-emerald-700 font-medium hover:text-emerald-800 flex items-center">
                          View all
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </a>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Video Tutorials */}
            <section ref={guidesRef} className="py-16 bg-slate-50">
              <div className="container mx-auto px-4">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center max-w-3xl mx-auto mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-slate-900" id="tutorials">
                    Video Tutorials
                  </h2>
                  <p className="text-lg text-slate-600">
                    Step-by-step video guides to help you navigate Verdea's features
                  </p>
                </motion.div>

                <div className="max-w-5xl mx-auto">
                  <div className="relative mb-8">
                    <div className="border-b border-slate-200 mb-4">
                      <div className="flex overflow-x-auto scrollbar-hide pb-2">
                        {videoTutorials.map((tutorial) => (
                          <button
                            key={tutorial.id}
                            className={`whitespace-nowrap px-4 py-2 mr-4 rounded-t-lg text-sm font-medium transition-colors ${
                              activeTutorial === tutorial.id
                                ? "border-b-2 border-emerald-600 text-emerald-700"
                                : "text-slate-600 hover:text-emerald-700"
                            }`}
                            onClick={() => setActiveTutorial(tutorial.id)}
                          >
                            {tutorial.title}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {videoTutorials.map((tutorial) => (
                      <div
                        key={tutorial.id}
                        className={`${activeTutorial === tutorial.id ? "block" : "hidden"}`}
                      >
                        <div className="grid md:grid-cols-2 gap-8 items-center">
                          <div>
                            <h3 className="text-2xl font-semibold mb-4 text-slate-900">{tutorial.title}</h3>
                            <p className="text-slate-600 mb-6">{tutorial.description}</p>
                            <div className="flex items-center text-sm text-slate-500 mb-8">
                              <Video className="h-4 w-4 mr-2" />
                              <span>{tutorial.duration}</span>
                            </div>
                            <a href={tutorial.videoUrl} target="_blank" rel="noopener noreferrer">
                              <Button className="bg-emerald-700 hover:bg-emerald-800">
                                Watch Tutorial
                                <ChevronRight className="ml-2 h-4 w-4" />
                              </Button>
                            </a>
                          </div>
                          <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg">
                            <img
                              src={tutorial.thumbnail}
                              alt={`${tutorial.title} thumbnail`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/20 hover:bg-slate-900/30 transition-colors group">
                              <div className="bg-white/90 rounded-full p-4 shadow-md group-hover:scale-110 transition-transform">
                                <Video className="h-8 w-8 text-emerald-700" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="text-center">
                    <a href="/help-center#tutorials" className="text-emerald-700 font-medium hover:text-emerald-800 inline-flex items-center">
                      View All Video Tutorials
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Guides */}
            <section className="py-16 bg-white">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center max-w-3xl mx-auto mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-slate-900">
                    Quick Guides
                  </h2>
                  <p className="text-lg text-slate-600">
                    Quick references for common tasks and questions
                  </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                  {quickGuides.map((guide, index) => {
                    const Icon = guide.icon;
                    return (
                      <motion.a
                        href={guide.path}
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-slate-200 flex flex-col h-full"
                      >
                        <div className="mb-4 p-3 bg-emerald-50 w-fit rounded-lg">
                          <Icon className="h-6 w-6 text-emerald-600" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2 text-slate-900 group-hover:text-emerald-700 transition-colors">{guide.title}</h3>
                        <p className="text-slate-600 text-sm mb-4 flex-grow">{guide.description}</p>
                        <div className="text-emerald-700 font-medium text-sm group-hover:translate-x-1 transition-transform flex items-center">
                          Read guide
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </div>
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Code Example */}
            <section className="py-16 bg-slate-50">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="max-w-4xl mx-auto"
                >
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200">
                    <div className="flex items-center justify-between bg-slate-100 px-6 py-3 border-b border-slate-200">
                      <div className="flex items-center">
                        <div className="flex space-x-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <span className="ml-4 text-sm font-medium text-slate-600">Example API Request</span>
                      </div>
                      <button className="text-slate-500 hover:text-slate-700">
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="bg-slate-900 p-6 font-mono text-sm text-emerald-300 overflow-auto">
                      <div className="text-slate-500">// Fetch a list of reports using the Verdea API</div>
                      <div className="mt-2 text-emerald-400">fetch</div>
                      <div>{'('}</div>
                      <div className="ml-4">'https://api.verdea.com/api/v1/reports',</div>
                      <div className="ml-4">{'{'}</div>
                      <div className="ml-8">method: <span className="text-amber-300">'GET'</span>,</div>
                      <div className="ml-8">headers: {'{'}</div>
                      <div className="ml-12">
                        <span className="text-amber-300">'Authorization'</span>: <span className="text-amber-300">'Bearer YOUR_API_KEY'</span>,
                      </div>
                      <div className="ml-12">
                        <span className="text-amber-300">'Content-Type'</span>: <span className="text-amber-300">'application/json'</span>
                      </div>
                      <div className="ml-8">{'}'}</div>
                      <div className="ml-4">{'}'}</div>
                      <div>{')'}</div>
<div className="ml-2">.then(response {'=>'} response.json())</div>
<div className="ml-2">.then(data {'=>'} console.log(data))</div>
<div className="ml-2">.catch(error {'=>'} console.error(error));</div>
                    </div>
                    <div className="bg-white px-6 py-4">
                      <a href="/api" className="text-emerald-700 font-medium hover:text-emerald-800 flex items-center">
                        View complete API documentation
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Call to Action Section */}
            <section className="py-16 bg-emerald-700 text-white">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="max-w-4xl mx-auto text-center"
                >
                  <h2 className="text-3xl font-bold mb-4">
                    Can't find what you're looking for?
                  </h2>
                  <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
                    Our support team is ready to help you with any questions you may have
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Link to="/help-center">
                      <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 min-w-[180px]">
                        Visit Help Center
                      </Button>
                    </Link>
                    <Link to="/contact">
                      <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600 min-w-[180px]">
                        Contact Support
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              </div>
            </section>
          </main>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DocumentationPage;
